import { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useOnboardingGate, onboardingGateQueryKey } from '@/hooks/useOnboardingGate';
import { useActivityTracker } from '@/hooks/useActivityTracker';
import AccessDenied from '@/components/AccessDenied';
import AuthLoadingSkeleton from '@/components/AuthLoadingSkeleton';
import LegalAcceptanceGate from '@/components/legal/LegalAcceptanceGate';
import DeskWelcome from '@/components/desk/DeskWelcome';
import SpotlightCue from '@/components/desk/SpotlightCue';
import DeskRibbon from '@/components/desk/DeskRibbon';
import { BelowHeaderSlotProvider } from '@/components/layout/BelowHeaderSlot';
import MissionReturnLink from '@/components/layout/MissionReturnLink';
import LotCelebration from '@/components/desk/LotCelebration';
import DeskCelebrations from '@/components/desk/DeskCelebrations';
import WatchLimitDialog from '@/components/watch/WatchLimitDialog';
import PushOnloadPrompt from '@/components/notifications/PushOnloadPrompt';
import { toast } from 'sonner';

/**
 * Route-level auth guard. Wrap all protected routes inside this layout.
 *
 * Uses a lightweight onboarding gate query (4 fields only) so we don't
 * block on company logos, admin roles, or subscription data.
 *
 * When arriving from Auth.tsx (fresh sign-in), the query cache is already
 * warm from prefetching — we read it synchronously to skip the skeleton frame.
 */
const ProtectedRoute = () => {
  const {
    user,
    loading: authLoading,
    initializing,
    signingOut,
    signOut
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const isSigningIn = sessionStorage.getItem('signing_in') === '1';

  // Fire-and-forget activity tracker — never affects rendering
  useActivityTracker(user?.id, undefined);
  const isOnboardingPage = location.pathname === '/onboarding';

  // Synchronous cache read: if Auth.tsx already prefetched, skip the skeleton
  const cachedGate = user ? queryClient.getQueryData<{
    onboardingComplete: boolean;
    profileExists?: boolean;
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
    companyId: string | null;
  }>(onboardingGateQueryKey(user.id)) : undefined;
  const isSyncReady = isOnboardingPage || !!cachedGate?.onboardingComplete || !!sessionStorage.getItem('onboardingComplete');

  // Lightweight query: only user profile fields needed for onboarding check
  const {
    data: gateData,
    isLoading: gateLoading,
    isFetched
  } = useOnboardingGate();

  // Reset onboarding check whenever the user identity changes.
  useEffect(() => {
    setOnboardingChecked(false);
  }, [user?.id]);
  useEffect(() => {
    if (isOnboardingPage) {
      setOnboardingChecked(true);
      return;
    }
    if (sessionStorage.getItem('onboardingComplete')) {
      setOnboardingChecked(true);
      return;
    }
    if (authLoading || initializing || !user) return;
    if (!isFetched || gateLoading) return;

    // Truly stale session: no public.users row exists for this auth user.
    // A row that exists but has blank onboarding fields is a brand-new or
    // freshly-invited user — they must complete onboarding, NOT be signed out.
    if (isFetched && gateData && gateData.profileExists === false) {
      console.warn('[ProtectedRoute] No profile row — signing out stale session');
      toast.error('Session expired', {
        description: 'Please sign in again.'
      });
      signOut();
      window.location.href = '/';
      return;
    }
    if (!gateData?.onboardingComplete) {
      const redirectPath = location.pathname + location.search;
      localStorage.setItem('postOnboardingRedirect', redirectPath);
      navigate('/onboarding', {
        replace: true
      });
      return;
    }
    sessionStorage.removeItem('signing_in');
    setOnboardingChecked(true);
  }, [user, authLoading, initializing, location.pathname, location.search, navigate, isOnboardingPage, gateData, gateLoading, isFetched]);

  // Consistent skeleton variant: keep "signing-in" message if that's what Auth.tsx set
  const skeletonVariant = isSigningIn ? 'signing-in' : 'loading';
  if (authLoading || initializing) {
    return <AuthLoadingSkeleton variant={skeletonVariant} />;
  }
  if (!user) {
    if (signingOut) {
      return <AuthLoadingSkeleton variant="signing-out" />;
    }
    return <AccessDenied variant="unauthenticated" />;
  }

  // Use synchronous cache OR effect-based check
  if (!isSyncReady && !onboardingChecked) {
    return <AuthLoadingSkeleton variant={skeletonVariant} />;
  }
  const deskSurfaces = !isOnboardingPage && !location.pathname.startsWith('/admin');
  return (
    <LegalAcceptanceGate>
      {/* Overlays (dialogs, celebrations, cue controller) take no space on the page. */}
      {deskSurfaces && (
        <>
          {/* First-login welcome follows the person to whatever page they land on. */}
          <DeskWelcome />
          <LotCelebration />
          <DeskCelebrations />
        </>
      )}
      <WatchLimitDialog />
      <SpotlightCue />
      {/* In-flow strips render below the fixed header via the page's AppHeader. */}
      <BelowHeaderSlotProvider
        value={
          <>
            <PushOnloadPrompt />
            {deskSurfaces && <DeskRibbon />}
            <MissionReturnLink />
          </>
        }
      >
        <Outlet />
      </BelowHeaderSlotProvider>
    </LegalAcceptanceGate>
  );
};
export default ProtectedRoute;